import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Steps, Button, Card, Typography, message, Space, Form, Modal } from 'antd';
import { RocketOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { MetadataStep } from './components/MetadataStep';
import { UnifiedContentEditor } from './components/UnifiedContentEditor';
import { ReviewStep } from './components/ReviewStep';
import { StoryRepository } from './StoryRepository';
import { StoryService } from './StoryService';
import { StoryChapter, CreateStoryInput, StoryOwnerType } from '@PikaDoApp/npm-pikado-logic/dist/modules/story/graphql/story.interface';
import { generateObjectId } from '../../utils';

const { Title, Text } = Typography;
const { Step } = Steps;

const storyRepo = new StoryRepository();
const storyService = new StoryService(storyRepo);

export const StoryCreate = () => {
    const history = useHistory();
    const [current, setCurrent] = useState(0);
    const [form] = Form.useForm();
    const [chapters, setChapters] = useState<Partial<StoryChapter>[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [storyId] = useState(() => generateObjectId());

    const validateContentStep = () => {
        if (!chapters || chapters.length === 0) {
            message.error('Please add at least one chapter');
            return false;
        }

        for (let i = 0; i < chapters.length; i++) {
            const ch = chapters[i];

            // 1. Validate Chapter (CreateChapterInput alignment)
            if (!ch.name?.en || !ch.name?.ar) {
                message.error(`Chapter ${i + 1} is missing a localized title (EN and AR required)`);
                return false;
            }
            if (typeof ch.order !== 'number') {
                message.error(`Chapter ${i + 1} has an invalid order number`);
                return false;
            }

            // 2. Validate Paragraphs (CreateParagraphInput alignment)
            if (!ch.paragraphs || ch.paragraphs.length === 0) {
                message.error(`Chapter ${i + 1} must have at least one paragraph`);
                return false;
            }

            for (let j = 0; j < ch.paragraphs.length; j++) {
                const p = ch.paragraphs[j];

                // Requirement: Localized text required (CreateParagraphInput.text)
                if (!p.text.en) {
                    message.error(`Paragraph ${j + 1} in Chapter ${i + 1} is missing English text (from transcription)`);
                    return false;
                }
                if (!p.text.ar) {
                    message.error(`Paragraph ${j + 1} in Chapter ${i + 1} is missing Arabic text`);
                    return false;
                }
                if (typeof p.order !== 'number') {
                    message.error(`Paragraph ${j + 1} in Chapter ${i + 1} has an invalid order number`);
                    return false;
                }

                // 3. Validate Words if present (ParagraphWordInput alignment)
                if (p.words) {
                    for (const w of p.words) {
                        if (!w.word || typeof w.start !== 'number' || typeof w.end !== 'number') {
                            message.error(`Transcription data error in Paragraph ${j + 1}, Chapter ${i + 1}`);
                            return false;
                        }
                    }
                }

                // 4. Validate Media if present (CreateMediaInput alignment)
                if (p.media) {
                    for (const m of p.media) {
                        if (!m.mediaUrl || !m.type) {
                            message.error(`Media missing required fields in Paragraph ${j + 1}, Chapter ${i + 1}`);
                            return false;
                        }
                    }
                }
            }
        }
        return true;
    };


    const handleBack = () => {
        Modal.confirm({
            title: 'Discard all data?',
            content: 'Are you sure you want to go back? All your progress in the Creator Studio will be lost.',
            okText: 'Yes, Discard',
            okType: 'danger',
            cancelText: 'No, Stay',
            onOk() {
                history.push('/stories');
            },
        });
    };

    const next = async () => {
        try {
            if (current === 0) {
                await form.validateFields();
            } else if (current === 1) {
                if (!validateContentStep()) return;
            }
            setCurrent(current + 1);
        } catch (error) {
            console.error('Validation failed:', error);
            message.error('Please fix the errors before proceeding');
        }
    };


    const prev = () => setCurrent(current - 1);

    const handleSubmit = async () => {
        try {
            setSubmitting(true);
            const metadata = form.getFieldsValue();
            console.log('📦 Form metadata collected:', metadata);

            // 1. Create Story with metadata
            const storyInput: CreateStoryInput = {
                ...metadata,
                _id: storyId,
                chapters: [], // Create with no chapters initially
                createdBy: 'admin',
                ownerType: metadata.ownerType || StoryOwnerType.PIKADO,
                title: metadata.title,
                subtitle: metadata.subtitle,
                description: metadata.description,
            };

            console.log('✅ Story Input prepared:', storyInput);
            const createdStory = await storyService.createStory(storyInput);
            console.log('✅ Story created on server:', createdStory._id);

            // 2. Add Chapters to the story
            if (chapters && chapters.length > 0) {
                console.log(`⏳ Adding ${chapters.length} chapters...`);
                for (const chapter of chapters) {
                    await storyService.addChapterToStory({
                        storyId: createdStory._id,
                        chapter: {
                            name: chapter.name!,
                            paragraphs: chapter.paragraphs as any,
                            order: chapter.order || 0,
                            chapterDuration: chapter.chapterDuration,
                        }
                    });
                }
            }

            message.success('Story and chapters created successfully!');
            history.push('/stories');
        } catch (error) {
            message.error('Failed to create story');
            console.error('Submit error:', error);
        } finally {
            setSubmitting(false);
        }
    };

    const steps = [
        {
            title: 'Metadata',
            content: <MetadataStep storyId={storyId} />,
        },
        {
            title: 'Content',
            content: <UnifiedContentEditor chapters={chapters} setChapters={setChapters} storyId={storyId} />,
        },
        {
            title: 'Review',
            content: <ReviewStep metadata={form.getFieldsValue()} chapters={chapters} />,
        },
    ];

    return (
        <div style={{
            padding: '40px 24px',
            background: '#f8fafc',
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
        }}>
            <div style={{ width: '100%', maxWidth: '1000px', marginBottom: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Button icon={<ArrowLeftOutlined />} onClick={handleBack} style={{ borderRadius: '8px' }}>
                    Back to Library
                </Button>
                <div style={{ textAlign: 'center', flex: 1 }}>
                    <Title level={2} style={{ margin: 0, background: 'linear-gradient(135deg, #1e3a8a 0%, #7c3aed 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                        Story Creator Studio
                    </Title>
                </div>
            </div>

            <Card bordered={false} style={{
                width: '100%',
                maxWidth: '1000px',
                borderRadius: '24px',
                boxShadow: '0 20px 50px rgba(0,0,0,0.05)',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                padding: '20px'
            }}>
                <Steps
                    current={current}
                    style={{ marginBottom: '40px', padding: '0 40px' }}
                    type="navigation"
                >
                    {steps.map(item => (
                        <Step key={item.title} title={item.title} />
                    ))}
                </Steps>

                <Form
                    form={form}
                    layout="vertical"
                    preserve={true}
                    onValuesChange={() => current === 2 && setCurrent(current)} // Force re-render of review if needed
                >
                    <div style={{ minHeight: '400px' }}>
                        {/* Step 0: Metadata */}
                        <div style={{ display: current === 0 ? 'block' : 'none' }}>
                            <MetadataStep storyId={storyId} />
                        </div>

                        {/* Step 1: Content */}
                        <div style={{ display: current === 1 ? 'block' : 'none' }}>
                            <UnifiedContentEditor chapters={chapters} setChapters={setChapters} storyId={storyId} />
                        </div>

                        {/* Step 2: Review */}
                        <div style={{ display: current === 2 ? 'block' : 'none' }}>
                            <ReviewStep metadata={form.getFieldsValue()} chapters={chapters} />
                        </div>
                    </div>
                </Form>

                <div style={{
                    marginTop: '40px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    borderTop: '1px solid #f1f5f9',
                    paddingTop: '24px'
                }}>
                    <Button
                        size="large"
                        onClick={prev}
                        disabled={current === 0}
                        style={{ borderRadius: '12px', minWidth: '120px' }}
                    >
                        Back
                    </Button>

                    <Space>
                        <Button
                            size="large"
                            onClick={handleBack}
                            style={{ borderRadius: '12px' }}
                        >
                            Cancel
                        </Button>
                        {current < steps.length - 1 ? (
                            <Button
                                type="primary"
                                size="large"
                                onClick={next}
                                style={{
                                    borderRadius: '12px',
                                    minWidth: '120px',
                                    background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
                                    border: 'none'
                                }}
                            >
                                Next
                            </Button>
                        ) : (
                            <Button
                                type="primary"
                                size="large"
                                onClick={handleSubmit}
                                loading={submitting}
                                icon={<RocketOutlined />}
                                style={{
                                    borderRadius: '12px',
                                    minWidth: '150px',
                                    background: 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)',
                                    border: 'none'
                                }}
                            >
                                Create Story
                            </Button>
                        )}
                    </Space>
                </div>
            </Card>
        </div>
    );
};
